/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */



import { Component, Input, Output, EventEmitter, ElementRef, Renderer, HostBinding, ViewEncapsulation } from '@angular/core';
import { AfterContentInit, AfterViewInit, OnInit, OnDestroy, ContentChildren, ViewChildren, ViewChild, ContentChild } from '@angular/core';
import { QueryList, Inject, Optional, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, COMPOSITION_BUFFER_MODE } from '@angular/forms';

import { Subject } from 'rxjs/Subject';
import'rxjs/add/operator/debounceTime';
import'rxjs/add/operator/distinctUntilChanged';

import { DomUtils } from '../util/dom.utils';
import { onNextFrame } from '../util/anim.frame';
import { transitionLeave } from '../animation/transition';
import { getPopupClassNameFromAlign, getAlignFromPlacement } from '../trigger/utils';

import KeyCode from '../util/key.code';
import classNames from 'classnames';
import align from 'dom-align';


import { FormControl } from '../input/form.control';
import { Option } from './option';
import { OptionGroup } from './option.group';
import { NotFound } from './not.found';

export type Child = Option | OptionGroup;

export interface Data {
    value: string;
    text?: string;
    disabled?: boolean;
    id?: string;
    children?: Data[];
};

const /** @type {?} */ INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => Select),
    multi: true
};

const /** @type {?} */ BUILT_IN_PLACEMENTS = {
    bottomLeft: {
        points: ['tl', 'bl'],
        offset: [0, 4],
        overflow: {
            adjustX: 0,
            adjustY: 1,
        },
    },
    topLeft: {
        points: ['bl', 'tl'],
        offset: [0, -4],
        overflow: {
            adjustX: 0,
            adjustY: 1,
        },
    },
};
/**
 * @param {?} value
 * @return {?}
 */
function toArray(value: any): any[] {
    let /** @type {?} */ ret = value;
    if (value === undefined) {
        ret = [];
    } else if (!Array.isArray(value)) {
        ret = [value];
    }
    return ret;
}
/**
 * Select 选择器
 * 参考: https://github.com/react-component/select
 */
export class Select extends FormControl implements OnInit, OnDestroy, AfterContentInit, AfterViewInit {
/**
 * 样式前缀
 */
prefixCls = 'ant-select';
/**
 * 用户CSS样式
 */
class: string;
/**
 * 控件大小。注：标准表单内的输入框大小限制为 `large`。可选 `large` `default` `small`。
 */
size: 'large' | 'default' | 'small' = 'default';
/**
 * 选择框默认文字
 */
placeholder: string;
/**
 * 在选择框中显示搜索框，默认为false
 */
showSearch = false;
/**
 * 支持清除，默认为false
 */
allowClear = false;
/**
 * 设置 Select 的模式。可选 `multiple` `tags` `combobox`。
 */
mode: 'multiple' | 'tags' | 'combobox';
/**
 * 是否默认高亮第一个选项
 */
defaultActiveFirstOption = true;
/**
 * 当下拉列表为空时显示的内容
 */
notFoundContent = '无匹配结果' || 'Not Found';
/**
 * 下拉菜单和选择器同宽
 */
dropdownMatchSelectWidth = true;
/**
 * 下拉菜单的 class 属性
 */
dropdownClass: string;

     dropdownPlacement = 'bottomLeft';
     builtinPlacements = BUILT_IN_PLACEMENTS;
/**
 * 自动查询延迟时间（ms），默认300毫秒
 */
filterDelay = 300;
/**
 * 是否根据输入项进行筛选。当其为一个函数时，会接收 `inputValue`, `option` 两个参数，当 `option` 符合筛选条件时，应返回 `true`，反之则返回 `false`。
 */
filterOption: boolean | ((inputValue: string, option: Option) => any) = true;
/**
 * 搜索时过滤对应的 option 属性，可选项如：value, text
 */
optionFilterProp = 'value';
/**
 * 展开/关闭菜单时的动画效果
 */
transitionName = 'slide-up';
     choiceTransitionName = 'zoom';
/**
 * 显示下拉三角
 */
showArrow = true;
/**
 * 选中后显示的标签最大长度
 */
maxTagTextLength: number;
/**
 * 搜索框的样式
 */
inputClass = '';
/**
 * 重新设置下拉选项列表
 * @param {?} data
 * @return {?}
 */
set data(data: Data[]) {
        this.setData(data);
    }
/**
 * option select 事件，参数为：(option: Option)
 */
select = new EventEmitter<any>();
/**
 * option deselect 事件，参数为：(option: Option)
 */
deselect = new EventEmitter<any>();
/**
 * search 事件
 */
search = new EventEmitter<any>();
/**
 * 菜单可视事件变更
 */
openChange = new EventEmitter<boolean>();
/**
 * 值变更事件
 */
change = new EventEmitter<any>();
/**
 * 搜索框
 */
input: ElementRef;
/**
 * 已选内容显示
 */
selectionEl: ElementRef;
/**
 * 菜单容器
 */
menuContainer: ElementRef;
public inputValue = '';
private _focused = false;
private searchTerms = new Subject<string>();

    // 自定义无选择项时的提醒内容
     notFound: NotFound;
public _optionGrpContentChildren: QueryList<OptionGroup>;
public _optionContentChildren: QueryList<Option>;
public _optionViewChildren: QueryList<Option>;
/**
 * 来源于ContentChildren（包含直属的选项和选项组），将最终用于生成实际View下的控件。目的：更新children即更新视图。
 */
public children: Child[] = [];
/**
 * 当前所有选项（包括可见和不可见的Option
 */
public options: Option[] = [];
/**
 * 当前已选中的选项
 */
public selectedOptions: Option[] = [];
/**
 * 当前已筛选的选项
 */
public filteredOptions: Option[] = [];
/**
 * 当前已激活的选项（鼠标移入或按上下方向键）
 */
public activedOption: Option;
/**
 * 在tags模式下，输入框可以输入任意内容，且该内容没有匹配的选项时，该内容将可以作为下拉菜单的第一个选项
 */
public inputTagOption: Option;
private initValue: any;
private isViewInited = false;
private _filtered = false;
private optionUUID = 0;
public open = false;
private _popupVisible = false;
private alignClassName: string;
private clickOutsideHandler: Function;
private touchOutsideHandler: Function;
private blurTimer: any;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} compositionMode
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private compositionMode: boolean) {

        super(renderer, elementRef, compositionMode);
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * 选择框主要容器部分
 * @return {?}
 */
getSelectionElement(): HTMLElement {
        return /** @type {?} */(( this.selectionEl.nativeElement as HTMLElement));
    }
/**
 * 选择项
 * @return {?}
 */
getMenuContainerElement(): HTMLElement {
        return /** @type {?} */(( this.menuContainer.nativeElement as HTMLElement));
    }
/**
 * @return {?}
 */
ngOnInit(): void {
        this.searchTerms
            .debounceTime(this.filterDelay)  // wait for 300ms pause in events
            .distinctUntilChanged()   // ignore if next search term is same as previous
            .subscribe((term: string) => {
                this.filterOptions();
                this.search.emit(term);
            });
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        this.clearBlurTime();
        this.clearOutsideHandler();
        this.options = null;
        this.selectedOptions = null;
        this.filteredOptions = null;
        this.activedOption = null;
        this.children = null;
    }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
        // 获取View重写后的数据选项
        const /** @type {?} */ children: Child[] = [];
        this._optionGrpContentChildren.forEach(item => children.push(item));
        this._optionContentChildren.forEach(item => children.push(item));
        this.setChildren(children);
    }
/**
 * @return {?}
 */
ngAfterViewInit(): void {
        onNextFrame(() => {
            // 获取View重写后的数据选项
            this.filteredOptions = this.options = this._optionViewChildren.toArray();
            // 根据ngModel传入的值选中对应的选项
            this.buildSelectedOptions();

            // 重建完毕
            this.isViewInited = true;
        });
    }
/**
 * @param {?} children
 * @return {?}
 */
setChildren(children: Child[]): void {
        // children将在HTML模板中生成新的Option和OptionGroup控件
        this.children = children;

        // 获取所有的option，并放入options和filteredOptions集合
        this.options = [];
        const /** @type {?} */ len = this.children.length;
        for (let /** @type {?} */ i = 0; i < len; i++) {
            const /** @type {?} */ child = this.children[i];
            if (child instanceof Option) {
                this.options.push(child);
            } else if (child instanceof OptionGroup) {
                this.options.push(...child.getOptions());
            }
        }
        this.filteredOptions = this.options;
        this.activedOption = null;

        // 根据ngModel传入的值选中对应的选项
        this.buildSelectedOptions();

        // 默认激活第一个选项
        this.activeFirstOption();
    }
/**
 * @param {?} data
 * @return {?}
 */
setData(data: Data[]): void {
        const /** @type {?} */ children: Child[] = [];
        data.forEach((d: Data) => {
            if (d.children) {
                const /** @type {?} */ optionGroup = this.newOptionGroup(d.text);
                d.children.forEach(c => {
                    const /** @type {?} */ option = this.newOption(c.value, c.text, c.id, c.disabled);
                    optionGroup.pushOption(option);
                });
                children.push(optionGroup);
            } else {
                const /** @type {?} */ option = this.newOption(d.value, d.text, d.id, d.disabled);
                children.push(option);
            }
        });

        this.setChildren(children);
    }
/**
 * 生成Option的ID，并返回
 * @param {?} option
 * @return {?}
 */
registerOption(option: Option): string {
        return `${this.id}-opt-${this.optionUUID++}`;
    }
/**
 * @param {?} event
 * @return {?}
 */
onKeyDown(event: KeyboardEvent) {
        if (this.disabled) {
            return;
        }
        if (this.isMultipleOrTagsOrCombobox()) { // only single will go
            return;
        }

        const /** @type {?} */ keyCode = event.keyCode;
        if (this.open && !this.input) {
            this.onInputKeyDown(event);
        } else if (keyCode === KeyCode.ENTER || keyCode === KeyCode.DOWN) {
            this.setOpen(true);
            event.preventDefault();
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onInputKeyDown(event: KeyboardEvent): void {
        if (this.disabled) {
            return;
        }

        const /** @type {?} */ keyCode = event.keyCode;
        const /** @type {?} */ inputValue = this.inputValue;

        // 按下BACKSPACE键，若已没有搜索字符，则删除最后一个标签
        if (this.isMultipleOrTags() && !inputValue && keyCode === KeyCode.BACKSPACE) {
            event.preventDefault();
            this.selectedOptions.pop(); // 删除最后一个标签
            this.onValueChange();
            return;
        }

        // 按ESC键关闭下拉菜单
        if (keyCode === KeyCode.ESC) {
            if (this.open) {
                this.setOpen(false);
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }

        // 按DOWN键打开下拉菜单
        if (keyCode === KeyCode.DOWN) {
            if (!this.open) {
                this.openIfHasChildren();
                event.preventDefault();
                event.stopPropagation();
                return;
            }
        }

        // 如果菜单已经打开，则按下向上、向下按键，则移动至相邻的选项
        if (this.open) {
            if (keyCode === KeyCode.DOWN || keyCode === KeyCode.UP) {
                event.preventDefault();
                event.stopPropagation();

                const /** @type {?} */ forward = keyCode === KeyCode.DOWN;
                const /** @type {?} */ nextOption = this.getNextOption(forward);
                if (nextOption) {
                    this.activedOption = nextOption;
                }
                if (keyCode === KeyCode.UP && !nextOption) {
                    this.setOpen(false);
                }
            }
            if (keyCode === KeyCode.ENTER) {
                event.preventDefault();
                event.stopPropagation();
                if (this.activedOption) {
                    this.onOptionClick({ event: event, option: this.activedOption });
                }
            }
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onInputKeyUp(event: KeyboardEvent): void {
        if (this.disabled) {
            return;
        }
        const /** @type {?} */ keyCode = event.keyCode;
        if (KeyCode.isCharacterKey(keyCode) || KeyCode.isTextModifyingKeyEvent(event)) {
            // 发射输入源的数据
            this.searchTerms.next(this.inputValue);
        }

        // 设置输入框的宽度
        if (this.isMultipleOrTags()) {
            const /** @type {?} */ inputElement: HTMLElement = this.input.nativeElement;
            const /** @type {?} */ mirriElement: HTMLElement = /** @type {?} */(( inputElement.nextElementSibling as HTMLElement));
            const /** @type {?} */ width = this.inputValue ? mirriElement.clientWidth + 'px' : '';
            this.renderer.setElementStyle(inputElement, 'width', width);
        }
    }
/**
 * 搜索框禁止change事件传播
 * @param {?} event
 * @return {?}
 */
onInputChange(event: Event): void {
        event.stopPropagation();
    }
/**
 * 搜索框的blur事件
 * @return {?}
 */
onInputBlur(): void {
        this.onOuterBlur();
    }
/**
 * @param {?} e
 * @return {?}
 */
onOuterFocus(e: Event): void {
        this.onTouched(); // set your control to 'touched'
        this.clearBlurTime();

        if (!this.isMultipleOrTagsOrCombobox() && this.isInputElement( /** @type {?} */((e.target as HTMLElement)))) {
            return;
        }
        if (this._focused) {
            return;
        }
        this._focused = true;
    }
/**
 * @param {?} target
 * @return {?}
 */
isInputElement(target: HTMLElement): boolean {
        return this.input && this.input.nativeElement === target;
    }
/**
 * @return {?}
 */
onOuterBlur(): void {
        this.blurTimer = setTimeout(() => {
            this._focused = false;
            const /** @type {?} */ inputValue = this.inputValue;

            if (this.isSingleMode() && this.showSearch &&
                inputValue && this.defaultActiveFirstOption) {
                const /** @type {?} */ firstOption = this.findFirstOption();
                if (firstOption) {
                    this.selectedOptions = [firstOption];
                    this.onValueChange();
                }
            } else if (this.isMultipleOrTags() && inputValue) {
                this.setInputValue('');
            }
        }, 10);
    }
/**
 * @return {?}
 */
maybeFocus(): void {
        const /** @type {?} */ input = this.input;
        const /** @type {?} */ activeElement = document.activeElement;
        if (input && (this.open || this.isMultipleOrTagsOrCombobox())) {
            if (input && input.nativeElement !== activeElement) {
                input.nativeElement.focus();
                this._focused = true;
            }
        } else {
            const /** @type {?} */ selectionEl = this.getSelectionElement();
            if (selectionEl && selectionEl !== activeElement) {
                selectionEl.focus();
                this._focused = true;
            }
        }
    }
/**
 * 根据ngModel传入的值选中对应的选项
 * @return {?}
 */
buildSelectedOptions(): void {
        // 根据ngModel传入的值选中对应的选项
        if (this.initValue) {
            this.selectedOptions = this.processValue(this.initValue);
            if (this.selectedOptions.length && this.isCombobox()) {
                this.inputValue = this.selectedOptions[0].text;
            }
        }
    }
/**
 * @param {?} value
 * @return {?}
 */
processValue(value: any): any {
        const /** @type {?} */ result = [];
        const /** @type {?} */ values = toArray(value);
        if (values.length) {
            for (let /** @type {?} */ i = 0; i < values.length; i++) {
                const /** @type {?} */ option = this.findOptionByValue(this.options, values[i]);
                if (option) {
                    result.push(option);
                }
            }
        }
        return result;
    }
/**
 * Write a new value to the element.
 * 
 * \@Override (From ControlValueAccessor interface)
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
        if (value == null) {
            return;
        }

        this.initValue = value;
        if (!this.isViewInited) {
            return; // View还没有创建的话无法获取到实际的Option列表，那么将要在ngAfterViewInit中再执行一次
        }
        this.buildSelectedOptions();
    }
/**
 * @return {?}
 */
getFormValue(): any {
        if (this.isSingleMode() || this.isCombobox()) {
            const /** @type {?} */ option = this.selectedOptions[0];
            return option && option.value;
        }

        const /** @type {?} */ values: string[] = [];
        this.selectedOptions.forEach(option => {
            values.push(option.value);
        });
        return values;
    }
/**
 * @return {?}
 */
onValueChange(): void {
        const /** @type {?} */ value = this.getFormValue();
        this.onChange(value);
        this.change.emit(value);
    }
/**
 * @param {?} e
 * @return {?}
 */
onArrowClick(e: MouseEvent): void {
        e.stopPropagation();
        if (!this.disabled) {
            this.setOpen(!this.open, !this.open);
        }
    }
/**
 * @return {?}
 */
onPlaceholderClick(): void {
        if (this.input) {
            this.input.nativeElement.focus();
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onTriggerClick(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        event.preventDefault();
        this.setPopupVisible(!this.isPopupVisible());
        this.maybeFocus();
    }
/**
 * @return {?}
 */
onPopupFocus(): void {
        // fix ie scrollbar, focus element again
        this.maybeFocus();
    }
/**
 * @param {?} event
 * @return {?}
 */
onClearSelection(event: Event): void {
        if (this.disabled) {
            return;
        }
        event.stopPropagation();

        this.setOpen(false, true);
        if (this.inputValue) {
            this.setInputValue('');
        }
        this.selectedOptions = [];
        this.onValueChange();
    }
/**
 * 点击选项
 * 
 * @param {?} event 点击事件
 * @return {?}
 */
onOptionClick(event: { event: Event, option: Option }): void {
        const /** @type {?} */ option: Option = event.option;
        const /** @type {?} */ multiple = this.isMultipleOrTags();

        if (this.isOptionSelected(option)) { // 已选择的，则移除
            if (multiple) {
                this.selectedOptions = this.selectedOptions.filter(opt => opt !== option);
                this.deselect.emit(option);
            }
        } else { // 未选择的，则添加选中
            if (multiple) {
                this.selectedOptions.push(option);
            } else {
                this.selectedOptions = [option];
                this.setOpen(false, true);
            }
            this.select.emit(option); // tag新增如何判断?
        }
        this.onValueChange();

        // 设置搜索框的文本值
        let /** @type {?} */ inputValue;
        if (this.isCombobox()) {
            inputValue = this.selectedOptions[0].text;
        } else {
            inputValue = '';
        }
        this.setInputValue(inputValue, false);
    }
/**
 * @param {?} option
 * @param {?} event
 * @return {?}
 */
removeSelectedOption(option: Option, event: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (this.disabled) {
            return;
        }

        // 执行动画
        const /** @type {?} */ el: HTMLElement = /** @type {?} */(( event.target as HTMLElement));
        const /** @type {?} */ optionEl: HTMLElement = /** @type {?} */(( el.closest('LI') as HTMLElement));
        transitionLeave(optionEl, this.choiceTransitionName, () => {
            // 移除已选列表中的选项
            this.selectedOptions = this.selectedOptions.filter(opt => opt !== option);

            // 发射deselect事件，若是多选框或Tags时
            const /** @type {?} */ multiple = this.isMultipleOrTags();
            if (multiple) {
                this.deselect.emit(option);
            }
            this.onValueChange();
        });
    }
/**
 * @param {?} inputValue
 * @param {?=} fireSearch
 * @return {?}
 */
setInputValue(inputValue: any, fireSearch = true): void {
        if (inputValue !== this.inputValue) {
            this.inputValue = inputValue;
            if (fireSearch) {
                this.searchTerms.next(inputValue);
            }
        }
    }
/**
 * 搜索过滤选项
 * @return {?}
 */
filterOptions(): void {
        if (!this.filterOption) {
            return;
        }

        onNextFrame(() => {
            this.filteredOptions = this.options.filter(this.filterOptionFunction(this.inputValue));
            this._filtered = true;

            // 默认激活第一个选项
            this.activeFirstOption();
        });
    }
/**
 * 内部搜索功能
 * @param {?} searchValue
 * @return {?}
 */
private filterOptionFunction(searchValue: string): (option: Option) => boolean {
        return (option: Option): boolean => {
            // 当搜索内容为空时，或者当filterOption为false时，直接返回所有的选项
            if (!searchValue || !this.filterOption) {
                return true;
            }

            if (this.filterOption && typeof this.filterOption === 'function') {
                return this.filterOption(this.inputValue, option);
            }

            const /** @type {?} */ filterValue = option[this.optionFilterProp];
            return String(filterValue).indexOf(this.inputValue) >= 0;
        };
    }
/**
 * 查找activedOption在已过滤选项中的前/后一个非disabled的选项
 * @param {?} forward
 * @return {?}
 */
getNextOption(forward: boolean): Option {
        const /** @type {?} */ len = this.filteredOptions.length;
        if (forward) {
            let /** @type {?} */ index = this.activedOption ? this.filteredOptions.indexOf(this.activedOption) : -1;
            index = index + 1;
            for (; index < len; index++) {
                const /** @type {?} */ option = this.filteredOptions[index];
                if (!option.disabled) {
                    return option;
                }
            }
            return null;
        } else {
            let /** @type {?} */ index = this.activedOption ? this.filteredOptions.indexOf(this.activedOption) : len;
            index = index - 1;
            for (; index >= 0; index--) {
                const /** @type {?} */ option = this.filteredOptions[index];
                if (!option.disabled) {
                    return option;
                }
            }
            return null;
        }
    }
/**
 * 查找已过滤选项中的第一个非disabled的选项
 * @return {?}
 */
findFirstOption(): Option {
        let /** @type {?} */ index = 0;
        const /** @type {?} */ len = this.filteredOptions.length;
        for (; index < len; index++) {
            const /** @type {?} */ option = this.filteredOptions[index];
            if (!option.disabled) {
                return option;
            }
        }
        return null;
    }
/**
 * @param {?} options
 * @param {?} value
 * @return {?}
 */
findOptionByValue(options: Option[], value: any): Option {
        let /** @type {?} */ optionFound = null;
        options.forEach(option => {
            if (option.value === value) {
                return !(optionFound = option);
            }
        });

        return optionFound;
    }
/**
 * @param {?} value
 * @return {?}
 */
getOptionByValue(value: any): Option {
        return this.findOptionByValue(this.filteredOptions, value);
    }
/**
 * @param {?} index
 * @param {?} option
 * @return {?}
 */
getOptionIdentify(index: number, option: Option): any {
        return option.id;
    }
/**
 * @return {?}
 */
getInputTagOption(): Option {
        if (this.isTagsMode() && this.inputValue.length
            && this.getOptionByValue(this.inputValue) == null) {
            if (!this.inputTagOption) {
                this.inputTagOption = this.newOption(this.inputValue);
            } else {
                this.inputTagOption.value = this.inputValue;
                this.inputTagOption.title = this.inputValue;
            }
        } else {
            this.inputTagOption = null;
        }

        return this.inputTagOption;
    }
/**
 * @param {?} value
 * @param {?=} text
 * @param {?=} id
 * @param {?=} disabled
 * @return {?}
 */
newOption(value: string, text?: string, id?: string, disabled = false): Option {
        const /** @type {?} */ option = new Option(this.renderer, null, this);
        option.value = value;
        option.title = text || value;
        option.id = id || this.registerOption(option);
        option.disabled = disabled;
        return option;
    }
/**
 * @param {?} text
 * @return {?}
 */
newOptionGroup(text: string): OptionGroup {
        const /** @type {?} */ optionGroup = new OptionGroup(this.renderer, null);
        optionGroup.label = text;
        return optionGroup;
    }
/**
 * 选项是否可以显示:
 * 由于filteredOptions来源于options集合，该集合为AfterView后所得；
 * 而传入的参option对象则来源于children集合，为AfterContent后所得，不属于相同的对象，只能用id比较。
 * @param {?} option
 * @return {?}
 */
isOptionFiltered(option: Option): boolean {
        if (!this._filtered) { // 还没有执行过滤，则全部选项都应该显示
            return true;
        }

        const /** @type {?} */ len = this.filteredOptions.length;
        for (let /** @type {?} */ i = 0; i < len; i++) {
            if (this.filteredOptions[i].id === option.id) {
                return true;
            }
        }
        return false;
    }
/**
 * 选项组是否可以显示（若选项组内所有的选项都不可视则该选项组不可视）
 * @param {?} optionGrp
 * @return {?}
 */
isOptionGroupFiltered(optionGrp: OptionGroup): boolean {
        const /** @type {?} */ options = optionGrp.getOptions();
        const /** @type {?} */ len = options.length;
        for (let /** @type {?} */ i = 0; i < len; i++) {
            if (this.isOptionFiltered(options[i])) {
                return true;
            }
        }
        return false;
    }
/**
 * @param {?} option
 * @return {?}
 */
isOptionSelected(option: Option): boolean {
        return this.selectedOptions.includes(option);
    }
/**
 * @param {?} option
 * @return {?}
 */
isOptionActived(option: Option): boolean {
        if (this.activedOption === option) {
            return true;
        }

        return this.activedOption && this.activedOption.id === option.id;
    }
/**
 * @return {?}
 */
activeFirstOption(): void {
        if (this.open && this.defaultActiveFirstOption) {
            const /** @type {?} */ option = this.getInputTagOption() || this.findFirstOption();
            if (option) {
                this.activedOption = option;
            }
        }
    }
/**
 * @return {?}
 */
isCombobox(): boolean {
        return this.mode === 'combobox';
    }
/**
 * 不是Multiple、Tags和Combobox
 * @return {?}
 */
isSingleMode(): boolean {
        return !this.isMultipleOrTagsOrCombobox();
    }
/**
 * 在tags模式下，输入框可以输入任意内容，且该内容没有匹配的选项时，该内容将可以作为下拉菜单的第一个选项
 * @return {?}
 */
isTagsMode(): boolean {
        return this.mode === 'tags';
    }
/**
 * @return {?}
 */
isMultipleOrTags(): boolean {
        return this.mode === 'multiple' || this.mode === 'tags';
    }
/**
 * @return {?}
 */
isMultipleOrTagsOrCombobox(): boolean {
        return this.isMultipleOrTags() || this.isCombobox();
    }
/**
 * @param {?} event
 * @return {?}
 */
preventDefaultEvent(event: Event): void {
        event.preventDefault();
    }
/**
 * @param {?} option
 * @return {?}
 */
getTagText(option: Option): string {
        let /** @type {?} */ content = option.text;
        const /** @type {?} */ maxTagTextLength = this.maxTagTextLength;
        if (maxTagTextLength &&
            typeof content === 'string' &&
            content.length > maxTagTextLength) {
            content = content.slice(0, maxTagTextLength) + '...';
        }
        return content;
    }
/**
 * 样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ classes = classNames(this.prefixCls, {
            [`${this.prefixCls}-lg`]: this.size === 'large',
            [`${this.prefixCls}-sm`]: this.size === 'small',
            [`${this.prefixCls}-open`]: this.open,
            [`${this.prefixCls}-focused`]: this.open || this._focused,
            [`${this.prefixCls}-combobox`]: this.isCombobox(),
            [`${this.prefixCls}-disabled`]: this.disabled,
            [`${this.prefixCls}-enabled`]: !this.disabled,
            [`${this.prefixCls}-allow-clear`]: this.allowClear
        }, this.class);
        return classes;
    }
/**
 * @return {?}
 */
getMenuClass(): any {
        return {
            [`${this.getDropdownPrefixCls()}-menu`]: 1,
            [`${this.getDropdownPrefixCls()}-menu-root`]: 1,
            [`${this.getDropdownPrefixCls()}-menu-vertical`]: 1,
            [`${this.getDropdownPrefixCls()}-menu-hidden`]: !this.open
        };
    }
/**
 * @return {?}
 */
getChoiceWrapClass(): any {
        return {
            [`${this.prefixCls}-selection__choice`]: 1,
            [`${this.prefixCls}-selection__choice__disabled`]: this.disabled
        };
    }
/**
 * 过滤时是否可以显示“没有匹配项”提示
 * @return {?}
 */
isNotFoundVisible(): boolean {
        return this.filterOption && (this.notFoundContent || this.notFound) && this.filteredOptions.length === 0;
    }
/**
 * Placeholder是否可视
 * @return {?}
 */
isPlaceholderVisible(): boolean {
        let /** @type {?} */ hidden = false;
        // 当搜索框内有内容时，隐藏Placeholder标签
        if (this.inputValue) {
            hidden = true;
        }
        // 当已经选择了标签时，隐藏Placeholder标签
        if (this.selectedOptions.length) {
            hidden = true;
        }

        return !hidden;
    }
/**
 * 清除内容按钮标记是否可视
 * @return {?}
 */
isClearVisible(): boolean {
        return this.allowClear &&
            (this.inputValue.length > 0 || this.selectedOptions.length > 0);
    }
/**
 * 下拉框已选内容是否可视（不可见时显示Placeholder）
 * @return {?}
 */
isSelectedValueVisible(): boolean {
        let /** @type {?} */ showSelectedValue = false;
        if (!this.showSearch) {
            showSelectedValue = true;
        } else {
            showSelectedValue = (this.open && !this.inputValue) || !this.open;
        }
        return showSelectedValue;
    }
/**
 * @return {?}
 */
getDropdownPrefixCls(): string {
        return `${this.prefixCls}-dropdown`;
    }
/**
 * @return {?}
 */
getMenuContainerClass(): any {
        const /** @type {?} */ dropdownPrefixCls = this.getDropdownPrefixCls();
        const /** @type {?} */ multiple = this.isMultipleOrTags();
        return {
            [`${dropdownPrefixCls}`]: 1,
            [`${dropdownPrefixCls}--multiple`]: multiple,
            [`${dropdownPrefixCls}--single`]: !multiple,
            [`${this.alignClassName}`]: this.alignClassName,
            [`${dropdownPrefixCls}-hidden`]: !this.open
        };
    }
/**
 * @return {?}
 */
getAlign(): {} {
        return getAlignFromPlacement(this.builtinPlacements, this.dropdownPlacement, {});
    }
/**
 * @return {?}
 */
forceAlign(): void {
        if (!this.disabled) {
            const /** @type {?} */ baseTarget = this.getSelectionElement(); // 基准组件，位置不会变
            const /** @type {?} */ alignTarget = this.getMenuContainerElement();

            // https://www.npmjs.com/package/dom-align
            const /** @type {?} */ alignConfig = this.getAlign();
            this.onAlign(align(alignTarget, baseTarget, alignConfig));
        }
    }
/**
 * @param {?} align
 * @return {?}
 */
onAlign(align: any): void {
        this.alignClassName = getPopupClassNameFromAlign(
            this.builtinPlacements, this.getDropdownPrefixCls(), align);
    }
/**
 * @return {?}
 */
isPopupVisible(): boolean {
        return this._popupVisible;
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
setPopupVisible(popupVisible: boolean): void {
        if (this.disabled) {
            return;
        }

        if (this._popupVisible !== popupVisible) {
            this._popupVisible = popupVisible;

            // We must listen to `mousedown` or `touchstart`, edge case:
            // https://github.com/ant-design/ant-design/issues/5804
            // https://github.com/react-component/calendar/issues/250
            // https://github.com/react-component/trigger/issues/50
            if (popupVisible) {
                if (!this.clickOutsideHandler) {
                    this.clickOutsideHandler = this.renderer.listen('document', 'mousedown', this.onDocumentClick.bind(this));
                }
                // always hide on mobile
                if (!this.touchOutsideHandler) {
                    this.touchOutsideHandler = this.renderer.listen('document', 'touchstart', this.onDocumentClick.bind(this));
                }
            }
            if (!popupVisible) {
                this.clearOutsideHandler();
            }

            this.setOpen(popupVisible);
            this.onDropdownVisibleChange(popupVisible);
            this.afterPopupVisibleChange(popupVisible);
        }
    }
/**
 * @param {?} open
 * @param {?=} needFocus
 * @return {?}
 */
setOpen(open: boolean, needFocus: boolean = false): void {
        if (this.open === open) {
            // this.maybeFocus(open, needFocus);
            return;
        }

        // clear search input value when open is false in singleMode.
        if (!open && this.isSingleMode() && this.showSearch) {
            this.setInputValue('');
        }
        if (!open) {
            // this.maybeFocus(open, needFocus);
        }

        this.open = open;
        this.setPopupVisible(open);
        this.openChange.emit(this.open);

        // 默认选中第一个
        this.activeFirstOption();
    }
/**
 * @return {?}
 */
openIfHasChildren() {
        if (this.isSingleMode() || this.filteredOptions.length) {
            this.setOpen(true);
        }
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
onDropdownVisibleChange(popupVisible: boolean) {
        if (popupVisible && !this._focused) {
            this.clearBlurTime();
            this._focused = true;
        }
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
afterPopupVisibleChange(popupVisible: boolean): void {
        if (popupVisible) {
            onNextFrame(() => {
                this.forceAlign();

                // calc dropdownMatchSelectWidth
                if (this.dropdownMatchSelectWidth) {
                    const /** @type {?} */ hostEl: HTMLElement = this.getHostElement();
                    const /** @type {?} */ popuEl: HTMLElement = this.getMenuContainerElement();
                    const /** @type {?} */ width = this.dropdownMatchSelectWidth ? 'width' : 'minWidth';
                    this.renderer.setElementStyle(popuEl, width, `${hostEl.offsetWidth}px`);
                }
            });
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onDocumentClick(event: MouseEvent): void {
        const /** @type {?} */ target = /** @type {?} */(( event.target as Node));
        const /** @type {?} */ root = this.getHostElement();
        const /** @type {?} */ popupEl = this.getMenuContainerElement();
        if (!DomUtils.contains(root, target) && !DomUtils.contains(popupEl, target)) {
            this.setPopupVisible(false);
            this.open = false;
        }
    }
/**
 * @return {?}
 */
clearOutsideHandler() {
        if (this.clickOutsideHandler) {
            this.clickOutsideHandler(); // Removes "listen" listener
            this.clickOutsideHandler = null;
        }

        if (this.touchOutsideHandler) {
            this.touchOutsideHandler(); // Removes "listen" listener
            this.touchOutsideHandler = null;
        }
    }
/**
 * @return {?}
 */
clearBlurTime(): void {
        if (this.blurTimer) {
            clearTimeout(this.blurTimer);
            this.blurTimer = null;
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-select',
    templateUrl: './select.html',
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [INPUT_CONTROL_VALUE_ACCESSOR],
    exportAs: 'select'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, decorators: [{ type: Inject, args: [Renderer, ] }, ]},
{type: ElementRef, decorators: [{ type: Inject, args: [ElementRef, ] }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [COMPOSITION_BUFFER_MODE, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'class': [{ type: Input },],
'size': [{ type: Input },],
'placeholder': [{ type: Input },],
'showSearch': [{ type: Input },],
'allowClear': [{ type: Input },],
'mode': [{ type: Input },],
'defaultActiveFirstOption': [{ type: Input },],
'notFoundContent': [{ type: Input },],
'dropdownMatchSelectWidth': [{ type: Input },],
'dropdownClass': [{ type: Input },],
'dropdownPlacement': [{ type: Input },],
'builtinPlacements': [{ type: Input },],
'filterDelay': [{ type: Input },],
'filterOption': [{ type: Input },],
'optionFilterProp': [{ type: Input },],
'transitionName': [{ type: Input },],
'choiceTransitionName': [{ type: Input },],
'showArrow': [{ type: Input },],
'maxTagTextLength': [{ type: Input },],
'inputClass': [{ type: Input },],
'data': [{ type: Input },],
'select': [{ type: Output },],
'deselect': [{ type: Output },],
'search': [{ type: Output },],
'openChange': [{ type: Output },],
'change': [{ type: Output },],
'input': [{ type: ViewChild, args: ['input', ] },],
'selectionEl': [{ type: ViewChild, args: ['selection', ] },],
'menuContainer': [{ type: ViewChild, args: ['menuContainer', ] },],
'notFound': [{ type: ContentChild, args: [NotFound, ] },],
'_optionGrpContentChildren': [{ type: ContentChildren, args: [OptionGroup, ] },],
'_optionContentChildren': [{ type: ContentChildren, args: [Option, ] },],
'_optionViewChildren': [{ type: ViewChildren, args: [Option, ] },],
'classes': [{ type: HostBinding, args: ['class', ] },],
};
}

function Select_tsickle_Closure_declarations() {
/** @type {?} */
Select.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Select.ctorParameters;
/** @type {?} */
Select.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Select.prototype.prefixCls;
/**
 * 用户CSS样式
 * @type {?}
 */
Select.prototype.class;
/**
 * 控件大小。注：标准表单内的输入框大小限制为 `large`。可选 `large` `default` `small`。
 * @type {?}
 */
Select.prototype.size;
/**
 * 选择框默认文字
 * @type {?}
 */
Select.prototype.placeholder;
/**
 * 在选择框中显示搜索框，默认为false
 * @type {?}
 */
Select.prototype.showSearch;
/**
 * 支持清除，默认为false
 * @type {?}
 */
Select.prototype.allowClear;
/**
 * 设置 Select 的模式。可选 `multiple` `tags` `combobox`。
 * @type {?}
 */
Select.prototype.mode;
/**
 * 是否默认高亮第一个选项
 * @type {?}
 */
Select.prototype.defaultActiveFirstOption;
/**
 * 当下拉列表为空时显示的内容
 * @type {?}
 */
Select.prototype.notFoundContent;
/**
 * 下拉菜单和选择器同宽
 * @type {?}
 */
Select.prototype.dropdownMatchSelectWidth;
/**
 * 下拉菜单的 class 属性
 * @type {?}
 */
Select.prototype.dropdownClass;
/** @type {?} */
Select.prototype.dropdownPlacement;
/** @type {?} */
Select.prototype.builtinPlacements;
/**
 * 自动查询延迟时间（ms），默认300毫秒
 * @type {?}
 */
Select.prototype.filterDelay;
/**
 * 是否根据输入项进行筛选。当其为一个函数时，会接收 `inputValue`, `option` 两个参数，当 `option` 符合筛选条件时，应返回 `true`，反之则返回 `false`。
 * @type {?}
 */
Select.prototype.filterOption;
/**
 * 搜索时过滤对应的 option 属性，可选项如：value, text
 * @type {?}
 */
Select.prototype.optionFilterProp;
/**
 * 展开/关闭菜单时的动画效果
 * @type {?}
 */
Select.prototype.transitionName;
/** @type {?} */
Select.prototype.choiceTransitionName;
/**
 * 显示下拉三角
 * @type {?}
 */
Select.prototype.showArrow;
/**
 * 选中后显示的标签最大长度
 * @type {?}
 */
Select.prototype.maxTagTextLength;
/**
 * 搜索框的样式
 * @type {?}
 */
Select.prototype.inputClass;
/**
 * option select 事件，参数为：(option: Option)
 * @type {?}
 */
Select.prototype.select;
/**
 * option deselect 事件，参数为：(option: Option)
 * @type {?}
 */
Select.prototype.deselect;
/**
 * search 事件
 * @type {?}
 */
Select.prototype.search;
/**
 * 菜单可视事件变更
 * @type {?}
 */
Select.prototype.openChange;
/**
 * 值变更事件
 * @type {?}
 */
Select.prototype.change;
/**
 * 搜索框
 * @type {?}
 */
Select.prototype.input;
/**
 * 已选内容显示
 * @type {?}
 */
Select.prototype.selectionEl;
/**
 * 菜单容器
 * @type {?}
 */
Select.prototype.menuContainer;
/** @type {?} */
Select.prototype.inputValue;
/** @type {?} */
Select.prototype._focused;
/** @type {?} */
Select.prototype.searchTerms;
/** @type {?} */
Select.prototype.notFound;
/** @type {?} */
Select.prototype._optionGrpContentChildren;
/** @type {?} */
Select.prototype._optionContentChildren;
/** @type {?} */
Select.prototype._optionViewChildren;
/**
 * 来源于ContentChildren（包含直属的选项和选项组），将最终用于生成实际View下的控件。目的：更新children即更新视图。
 * @type {?}
 */
Select.prototype.children;
/**
 * 当前所有选项（包括可见和不可见的Option
 * @type {?}
 */
Select.prototype.options;
/**
 * 当前已选中的选项
 * @type {?}
 */
Select.prototype.selectedOptions;
/**
 * 当前已筛选的选项
 * @type {?}
 */
Select.prototype.filteredOptions;
/**
 * 当前已激活的选项（鼠标移入或按上下方向键）
 * @type {?}
 */
Select.prototype.activedOption;
/**
 * 在tags模式下，输入框可以输入任意内容，且该内容没有匹配的选项时，该内容将可以作为下拉菜单的第一个选项
 * @type {?}
 */
Select.prototype.inputTagOption;
/** @type {?} */
Select.prototype.initValue;
/** @type {?} */
Select.prototype.isViewInited;
/** @type {?} */
Select.prototype._filtered;
/** @type {?} */
Select.prototype.optionUUID;
/** @type {?} */
Select.prototype.open;
/** @type {?} */
Select.prototype._popupVisible;
/** @type {?} */
Select.prototype.alignClassName;
/** @type {?} */
Select.prototype.clickOutsideHandler;
/** @type {?} */
Select.prototype.touchOutsideHandler;
/** @type {?} */
Select.prototype.blurTimer;
/** @type {?} */
Select.prototype.renderer;
/** @type {?} */
Select.prototype.elementRef;
/** @type {?} */
Select.prototype.compositionMode;
}






interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
